import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Input, Select, message, Statistic, Row, Col, Card, Button, Space } from 'antd';
import { MessageOutlined, UserOutlined, ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { messageLogAPI } from '../services/api';
import type { ColumnsType } from 'antd/es/table';

const { Search } = Input;
const { Option } = Select;

interface GroupMessageModalProps {
  visible: boolean;
  onCancel: () => void;
  groupId: number | string;
  groupTitle: string;
}

interface Message {
  id: number;
  bot_id: number;
  message_id: number;
  chat_id: string;
  chat_type: string;
  from_id: number;
  from_username?: string;
  from_first_name?: string;
  from_last_name?: string;
  message_type: string;
  text?: string;
  caption?: string;
  is_bot: boolean;
  create_time: string;
}

interface Stats {
  total_messages: number;
  total_users: number;
  first_message_time: string;
  last_message_time: string;
  typeStats: Array<{
    message_type: string;
    type_count: number;
  }>;
}

const GroupMessageModal: React.FC<GroupMessageModalProps> = ({
  visible,
  onCancel,
  groupId,
  groupTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [filters, setFilters] = useState({
    messageType: '',
    fromUsername: '',
  });

  // 获取消息记录
  const fetchMessages = async (page = 1) => {
    try {
      setLoading(true);
      const response = await messageLogAPI.getByChat(groupId, {
        page,
        limit: pagination.pageSize,
        ...filters,
      });

      if (response.data.success) {
        setMessages(response.data.data.messages);
        setPagination({
          ...pagination,
          current: response.data.data.pagination.page,
          total: response.data.data.pagination.total,
        });
      }
    } catch (error: any) {
      message.error('获取消息记录失败：' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 获取统计信息
  const fetchStats = async () => {
    try {
      const response = await messageLogAPI.getStats(groupId);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error: any) {
      console.error('获取统计失败:', error);
    }
  };

  // 刷新数据
  const handleRefresh = async () => {
    message.loading('正在刷新...', 0.5);
    await Promise.all([
      fetchMessages(pagination.current),
      fetchStats()
    ]);
    message.success('刷新成功');
  };

  // 初始加载
  useEffect(() => {
    if (visible) {
      fetchMessages(1);
      fetchStats();
    }
  }, [visible, filters]);

  // 表格列配置
  const columns: ColumnsType<Message> = [
    {
      title: '时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 180,
      render: (time: string) => new Date(time).toLocaleString('zh-CN'),
    },
    {
      title: '发送者',
      key: 'sender',
      width: 200,
      render: (_: any, record: Message) => (
        <div>
          <div>
            {record.from_first_name} {record.from_last_name}
            {record.is_bot && <Tag color="blue" style={{ marginLeft: 8 }}>BOT</Tag>}
          </div>
          {record.from_username && (
            <div style={{ fontSize: 12, color: '#999' }}>@{record.from_username}</div>
          )}
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'message_type',
      key: 'message_type',
      width: 100,
      render: (type: string) => {
        const typeMap: Record<string, { color: string; text: string }> = {
          text: { color: 'default', text: '文本' },
          photo: { color: 'blue', text: '图片' },
          video: { color: 'purple', text: '视频' },
          document: { color: 'orange', text: '文件' },
          sticker: { color: 'cyan', text: '贴纸' },
          voice: { color: 'green', text: '语音' },
          audio: { color: 'green', text: '音频' },
          location: { color: 'red', text: '位置' },
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '内容',
      key: 'content',
      ellipsis: true,
      render: (_: any, record: Message) => {
        const content = record.text || record.caption || '-';
        return (
          <div style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {content}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <span>群组消息记录 - {groupTitle}</span>
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            刷新
          </Button>
        </Space>
      }
      visible={visible}
      onCancel={onCancel}
      width={1200}
      footer={null}
      destroyOnClose
    >
      {/* 统计信息 */}
      {stats && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="总消息数"
                value={stats.total_messages}
                prefix={<MessageOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="发言人数"
                value={stats.total_users}
                prefix={<UserOutlined />}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="首条消息"
                value={new Date(stats.first_message_time).toLocaleDateString('zh-CN')}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="最新消息"
                value={new Date(stats.last_message_time).toLocaleDateString('zh-CN')}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ fontSize: 16 }}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* 过滤器 */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 16 }}>
        <Select
          placeholder="消息类型"
          style={{ width: 150 }}
          allowClear
          value={filters.messageType || undefined}
          onChange={(value) => setFilters({ ...filters, messageType: value || '' })}
        >
          <Option value="">全部类型</Option>
          <Option value="text">文本</Option>
          <Option value="photo">图片</Option>
          <Option value="video">视频</Option>
          <Option value="document">文件</Option>
          <Option value="sticker">贴纸</Option>
        </Select>

        <Search
          placeholder="搜索用户名"
          allowClear
          style={{ width: 250 }}
          onSearch={(value) => setFilters({ ...filters, fromUsername: value })}
        />
      </div>

      {/* 消息列表 */}
      <Table
        columns={columns}
        dataSource={messages}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 条消息`,
          onChange: (page) => fetchMessages(page),
        }}
        scroll={{ y: 400 }}
      />
    </Modal>
  );
};

export default GroupMessageModal;
